import React from "react";
import "./MetadataDisplay.css";

console.log("✅ MetadataDisplay.js is being loaded!");

const MetadataDisplay = ({ metadata }) => {
  // URL pattern to detect clickable links.
  const urlPattern = /^(https?:\/\/[^\s]+)/;

  // Helper function to render a document preview based on the file type.
  const renderDocumentPreview = (url) => {
    if (!url) return <p>No preview available</p>;

    console.log("Preview URL:", url);
    const lowerUrl = url.toLowerCase();

    // Wrap the preview in a container that also shows a clickable link for debugging.
    if (lowerUrl.endsWith(".pdf")) {
      return (
        <div className="preview-container">
          <a href={url} target="_blank" rel="noopener noreferrer">
            Open PDF in new tab
          </a>
          <embed
            src={url}
            type="application/pdf"
            className="preview-embed"
            width="100%"
            height="400px"
          />
        </div>
      );
    } else if (
      lowerUrl.endsWith(".png") ||
      lowerUrl.endsWith(".jpg") ||
      lowerUrl.endsWith(".jpeg") ||
      lowerUrl.endsWith(".gif") ||
      lowerUrl.endsWith(".bmp") ||
      lowerUrl.endsWith(".svg")
    ) {
      return (
        <div className="preview-container">
          <a href={url} target="_blank" rel="noopener noreferrer">
            Open Image in new tab
          </a>
          <img
            src={url}
            alt="Document Preview"
            className="preview-image"
            onError={(e) => {
              console.error("Error loading image preview:", url);
              e.target.style.display = "none";
              e.target.parentNode.innerHTML = "<p>Preview not available</p>";
            }}
          />
        </div>
      );
    } else {
      return (
        <div className="preview-container">
          <a href={url} target="_blank" rel="noopener noreferrer">
            Open Document in new tab
          </a>
          <iframe
            src={url}
            title="Document Preview"
            className="preview-iframe"
            width="100%"
            height="400px"
            frameBorder="0"
          />
        </div>
      );
    }
  };

  // Recursive helper to render values in a formatted and readable way.
  const renderFormattedValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") {
      if (urlPattern.test(value)) {
        return (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        );
      }
      return value;
    }
    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    if (Array.isArray(value)) {
      if (value.length > 3) {
        return (
          <details className="nested-details">
            <summary>Show list details</summary>
            <ul className="formatted-list">
              {value.map((item, index) => (
                <li key={index}>{renderFormattedValue(item)}</li>
              ))}
            </ul>
          </details>
        );
      } else {
        return (
          <ul className="formatted-list">
            {value.map((item, index) => (
              <li key={index}>{renderFormattedValue(item)}</li>
            ))}
          </ul>
        );
      }
    }
    if (typeof value === "object") {
      const keys = Object.keys(value);
      if (keys.length > 3) {
        return (
          <details className="nested-details">
            <summary>Show object details</summary>
            <ul className="formatted-object">
              {keys.map((key) => (
                <li key={key}>
                  <strong>{key}:</strong> {renderFormattedValue(value[key])}
                </li>
              ))}
            </ul>
          </details>
        );
      } else {
        return (
          <ul className="formatted-object">
            {keys.map((key) => (
              <li key={key}>
                <strong>{key}:</strong> {renderFormattedValue(value[key])}
              </li>
            ))}
          </ul>
        );
      }
    }
    return String(value);
  };

  const handleDownload = (item) => {
    const fileData = JSON.stringify(item, null, 2);
    const blob = new Blob([fileData], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = item.file_name + "-metadata.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = (item) => {
    const printWindow = window.open("", "_blank", "width=600,height=400");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Metadata</title>
          <style>
            body { font-family: sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; white-space: pre-wrap; }
          </style>
        </head>
        <body>
          <h2>Metadata for ${item.file_name}</h2>
          <table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              ${Object.entries(item.metadata)
                .map(
                  ([key, value]) => `
                <tr>
                  <td>${key}</td>
                  <td>${
                    value && typeof value === "object"
                      ? JSON.stringify(value, null, 2)
                      : String(value)
                  }</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handleShare = async (item) => {
    const shareData = {
      title: "Metadata for " + item.file_name,
      text: JSON.stringify(item.metadata, null, 2),
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        alert("Share feature is not supported in your browser. Please use the copy button.");
      }
    } catch (error) {
      console.error("Error sharing metadata:", error);
    }
  };

  const handleCopy = (item) => {
    const text = JSON.stringify(item.metadata, null, 2);
    navigator.clipboard
      .writeText(text)
      .then(() => alert("Metadata copied to clipboard!"))
      .catch((err) => console.error("Error copying metadata:", err));
  };

  // Define the ordered keys as per your requirements.
  // We want the title to appear as the first field in the table.
  const orderedFields = [
    "title",
    "author",
    "date",
    "importantNamesAndEvents",
    "keyTopics",
    "summary"
  ];

  // Render the metadata table in the desired order using case-insensitive matching.
  const renderMetadataTable = (metadataObj) => {
    const rows = [];
    // Add the ordered fields first.
    orderedFields.forEach((field) => {
      const key = Object.keys(metadataObj).find(
        (k) => k.toLowerCase() === field.toLowerCase()
      );
      if (key !== undefined) {
        rows.push(
          <tr key={key}>
            <td>{key.charAt(0).toUpperCase() + key.slice(1)}</td>
            <td>{renderFormattedValue(metadataObj[key])}</td>
          </tr>
        );
      }
    });
    // Add any remaining keys that weren't in the ordered list.
    Object.keys(metadataObj).forEach((key) => {
      if (!orderedFields.some((f) => f.toLowerCase() === key.toLowerCase())) {
        rows.push(
          <tr key={key}>
            <td>{key}</td>
            <td>{renderFormattedValue(metadataObj[key])}</td>
          </tr>
        );
      }
    });
    return (
      <table className="metadata-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    );
  };

  return (
    <div className="metadata-display">
      <h2>Extracted Metadata</h2>
      {metadata.map((item, index) => (
        <div key={index} className="metadata-card">
          <details>
            <summary>
              <h3>{item.metadata.title || item.file_name}</h3>
              {(item.metadata.author ||
                item.metadata.date ||
                item.metadata.confidence) && (
                <div className="card-header-details">
                  {item.metadata.author && (
                    <span className="author">{item.metadata.author}</span>
                  )}
                  {item.metadata.date && (
                    <span className="date">{item.metadata.date}</span>
                  )}
                  {item.metadata.confidence && (
                    <span className="confidence">{item.metadata.confidence}</span>
                  )}
                </div>
              )}
            </summary>
            <div className="metadata-content">
              <div className="document-preview">
                {renderDocumentPreview(item.metadata.preview || item.preview)}
              </div>
              <div className="metadata-table-container">
                {renderMetadataTable(item.metadata)}
              </div>
            </div>
            <div className="card-actions">
              <button onClick={() => handleDownload(item)}>💾 Download</button>
              <button onClick={() => handlePrint(item)}>🖨️ Print</button>
              <button onClick={() => handleShare(item)}>🔗 Share</button>
              <button onClick={() => handleCopy(item)}>📋 Copy</button>
            </div>
          </details>
        </div>
      ))}
    </div>
  );
};

export default MetadataDisplay;
